import { OVERRIDE_FIELDS, OVERRIDE_WORDS, overridableFields, titleCase, type CardChange, type CarryGroup, type CarryToldDto, type OverrideField, type ProcessStepDto, type StepCardDto, type StepMasterDto, type TaskOwner } from '@nsb/core';
import type { ProcessModel } from './useProcess.ts';

/**
 * A project's step card, the rules of it (design study 25, "Carry a Change Back", as Jason answered it on 2026-10-08):
 * the card as the project has the step, what a form changes of it, which fields are the project's own against the
 * master, whether the master can be updated from here, and the warning's groups. Pure, so the web tests hold them.
 */

/** The card as it is being filled in: its fields, and its checklist's items added (by their words) and taken off (by id). */
export interface CardForm {
  title: string;
  why: string;
  clientNote: string;
  owner: TaskOwner | null;
  trade: string | null;
  switches: Partial<Record<OverrideField, boolean>>;
  add: string[];
  off: string[];
}

/** An item on the step's checklist: a hold its switches put there is not taken off by hand. */
export interface CardItem {
  id: string;
  title: string;
  hold: boolean;
  done: boolean;
}

/** The step's card as the project has it now (the job's draft on it, on the setup page). */
export function cardOf(model: ProcessModel, s: ProcessStepDto): CardForm {
  const node = s.key ? model.tree.node(s.key) : undefined;
  const switches: Partial<Record<OverrideField, boolean>> = {};
  for (const f of node ? overridableFields(node) : []) switches[f] = Boolean(node![f]);
  return { title: s.title, why: s.why ?? '', clientNote: node?.clientNote ?? '', owner: s.owner, trade: s.trade, switches, add: [], off: [] };
}

/** Its checklist's items on the job, in the order of its list. */
export function cardItems(model: ProcessModel, s: ProcessStepDto): CardItem[] {
  const node = s.key ? model.tree.node(s.key) : undefined;
  const fields = node ? overridableFields(node) : [];
  return model.dto.steps
    .filter((x) => x.parentId === s.id && !x.off)
    .map((x) => ({
      id: x.id,
      title: x.title,
      hold: x.kind === 'inspection' && ((x.owner === 'authority' && fields.includes('inspection')) || (x.owner === 'nsb' && fields.includes('preInstall'))),
      done: x.state === 'done',
    }));
}

const words = (v: string) => v.trim();

/** What a form changes of the card as the project has it: only what differs, as the API takes it. */
export function cardChange(was: CardForm, now: CardForm): CardChange {
  const c: CardChange = {};
  if (words(now.title) !== was.title) c.title = words(now.title);
  if (words(now.why) !== words(was.why)) c.why = words(now.why) || null;
  if (words(now.clientNote) !== words(was.clientNote)) c.clientNote = words(now.clientNote);
  if (now.owner !== was.owner) c.owner = now.owner;
  if (now.trade !== was.trade) c.trade = now.trade;
  for (const f of OVERRIDE_FIELDS) {
    if (now.switches[f] !== undefined && Boolean(now.switches[f]) !== Boolean(was.switches[f])) c.switches = { ...c.switches, [f]: Boolean(now.switches[f]) };
  }
  const add = now.add.map(words).filter(Boolean);
  if (add.length) c.add = add;
  if (now.off.length) c.off = now.off;
  return c;
}

/** Who does it, as the card labels it by its kind: a gate is approved, a decision decided. */
export const ownerLabel = (kind: ProcessStepDto['kind']) => (kind === 'gate' ? 'Approved by' : kind === 'decision' ? 'Who decides' : 'Who does it');

/** What a change touches of the card, by the labels on it, for the bar of changes not saved (P-29). */
export function cardChangeNames(c: CardChange, kind: ProcessStepDto['kind']): string[] {
  const names: string[] = [];
  if (c.title !== undefined) names.push('Title');
  if (c.why !== undefined) names.push('For NSB');
  if (c.clientNote !== undefined) names.push('For the client');
  if (c.owner !== undefined) names.push(ownerLabel(kind));
  if (c.trade !== undefined) names.push('Its trade');
  if (c.add?.length || c.off?.length) names.push('Checklist');
  for (const f of OVERRIDE_FIELDS) if (c.switches?.[f] !== undefined) names.push(`${OVERRIDE_WORDS[f].charAt(0).toUpperCase()}${OVERRIDE_WORDS[f].slice(1)}`);
  return names;
}

/** Whether a field of the card is the master's, or the project's own: read against the master's card as it stands. */
export function isOwn(field: 'title' | 'why' | 'clientNote' | 'owner' | 'trade' | OverrideField, form: CardForm, master: StepCardDto | null | undefined): boolean {
  if (!master) return false;
  switch (field) {
    case 'title':
      return words(form.title) !== master.title;
    case 'why':
      return words(form.why) !== words(master.why ?? '');
    case 'clientNote':
      return words(form.clientNote) !== words(master.clientNote ?? '');
    case 'owner':
      return form.owner !== master.owner;
    case 'trade':
      return form.trade !== master.trade;
    default:
      return Boolean(form.switches[field]) !== Boolean(master.switches[field]);
  }
}

/**
 * Whether Save, and update the master tree can be pressed, and why not, in words for its hover and the line under it:
 * an admin's alone (q-rights A); refused while the master's own changes wait to be saved for projects (q-pending B).
 */
export function masterButton(admin: boolean, master: StepMasterDto | undefined): { disabled: boolean; why: string | null } {
  if (!admin) return { disabled: true, why: 'Only an admin can update the master tree.' };
  if (!master) return { disabled: true, why: 'Reading the master tree…' };
  if (master.pending) return { disabled: true, why: master.pending };
  return { disabled: false, why: null };
}

/** Each group of the warning (q-warn B), what it is and what happens there. */
export const GROUP_WORDS: Record<CarryGroup, { title: string; line: string }> = {
  follow: { title: 'Will follow it', line: 'Nothing on the step was changed there: it takes the change.' },
  keep: { title: 'Keep their own', line: 'Something on the step was changed and saved there: it does not get the change.' },
  filed: { title: 'Have it filed away', line: 'It follows while filed, and comes back with it.' },
  started: { title: 'Started or done there', line: 'The new words reach it; work the change adds or takes away does not.' },
};

export interface Warning {
  groups: { group: CarryGroup; title: string; line: string; names: string[] }[];
  /** Test projects, listed apart (q-test B), each with what happens there. */
  tests: { name: string; what: string }[];
  /** Whose clients read new words the next time they open the step (q-client A); nobody is emailed for words. */
  clients: string[];
  /**
   * Whose clients will be told a step waits on them (Jason, 2026-10-08, "Let it notify, name it"): each project where a
   * step that follows the master becomes theirs and the work has reached it, this one too (its card saved on its step at
   * once), read from the carry-back made and rolled back (`CarryToldDto`); null while that is still being read.
   */
  told: CarryToldDto['told'] | null;
}

/**
 * The warning before the master changes (q-warn B, with Jason's note: "only a completely unmodified step on the
 * projects process tree will follow the updated master"): the other projects with the step, in groups by what will
 * happen there, test projects apart, whose clients will read new words, when the step's words a client reads change,
 * and whose clients will be told a step waits on them (`told`, read from the API before Confirm).
 */
export function warningOf(master: StepMasterDto, form: CardForm, told: CarryToldDto | null = null): Warning {
  const order: CarryGroup[] = ['follow', 'keep', 'filed', 'started'];
  const real = master.projects.filter((p) => !p.test);
  const groups = order.map((g) => ({ group: g, ...GROUP_WORDS[g], names: real.filter((p) => p.group === g).map((p) => p.name) })).filter((g) => g.names.length);
  const tests = master.projects.filter((p) => p.test).map((p) => ({ name: p.name, what: GROUP_WORDS[p.group].title.toLowerCase() }));
  const readByClients = Boolean(master.master) && (isOwn('title', form, master.master) || isOwn('clientNote', form, master.master));
  const clients = readByClients ? master.projects.filter((p) => p.clientReads && p.group !== 'keep').map((p) => p.name) : [];
  return { groups, tests, clients, told: told?.told ?? null };
}

/**
 * What the warning says of the clients, in sentences (Jason, 2026-10-08, "Let it notify, name it"): whose clients read
 * new words the next time they open the step (q-client A), and whose clients will be told a step waits on them, by
 * email as often as each of them chose, the step named, or each project's where it is not this one (a test project
 * marked so; the project carrying it back named as this project). "Nobody is emailed" only where nobody will be, this
 * project's client counted: never while whose clients are told is still being read. `step` is the step being carried
 * back, by its title on the card.
 */
export function clientLines(w: Pick<Warning, 'clients' | 'told'>, step: string): string[] {
  const out: string[] = [];
  const told = w.told ?? [];
  if (w.clients.length) out.push(`Their clients read the new words the next time they open the step: ${w.clients.join(', ')}.${w.told && !told.length ? ' Nobody is emailed.' : ''}`);
  if (told.length) {
    const name = stepWords(step.trim() || 'it');
    const same = told.every((t) => t.steps.length === 1 && t.steps[0] === name);
    const names = told.map((t) => {
      const about = [t.test ? 'a test project' : null, same ? null : t.steps.join(', ')].filter(Boolean);
      const who = t.here ? 'this project' : t.name;
      return about.length ? `${who} (${about.join('; ')})` : who;
    });
    out.push(`Their clients will be told ${same ? `${name} waits` : 'what now waits'} on them, by email as often as each of them chose: ${names.join(', ')}.`);
  }
  return out;
}

/**
 * The line over the card: whether the step follows the master, or keeps its own since when, and whether its waits are
 * this project's own (drawn on it, design study 31), which a save of the master leaves as they are.
 */
export function followsLine(s: ProcessStepDto, day: (iso: string) => string): string {
  const waits = Boolean(s.follows?.waits);
  if (!s.follows || s.follows.follows) {
    return waits ? 'Follows the master tree, but for its waits, drawn on this project: a save of the master leaves them as they are until given back.' : 'Follows the master tree: a save of the master reaches it.';
  }
  return `This project’s own${s.follows.since ? ` since ${day(s.follows.since)}` : ''}${waits ? ', its waits too' : ''}: a save of the master does not reach it until it is given back.`;
}

/** Whether the card offers Back to the master's: the step keeps its own, or its waits are this project's. */
export const givesBack = (s: ProcessStepDto): boolean => s.follows?.follows === false || Boolean(s.follows?.waits);

/** The step named, as a running sentence names one. */
export const stepWords = (title: string) => titleCase(title);
